import { Controller } from "@hotwired/stimulus"
import { OverlayScrollbars } from "overlayscrollbars"
import { useZoomListener } from "./mixins/use_zoom_listener"
import { useStableLayout } from "./mixins/use_stable_layout"

/**
 * HorizontalScrollController replaces the browser's native scrollbar for horizontal scrolling.
 *
 * Its main features are:
 * - it is always visible (the native one hides after a while on mobile devices)
 * - when the user scrolls inside the container, the scroll bar is glued
 *   to the bottom edge of the viewport in each position, so the user can see at any moment
 *   that part of the container content is not visible
 *
 * Application:
 * - select an element to be horizontally scrollable
 * - enclose this element in a div element:
 *    %div{data: { controller: 'horizontal-scroll' }}
 * - add a detector to the end of the element, necessary for the scroll bar to work properly:
 *    %div{data: { horizontal_scroll_target: 'verticalDetector' }}
 *
 * So, for example:
 *  %div{data: { controller: 'horizontal-scroll'}}
 *    %table
 *      %thead
 *      %tbody
 *   %div{data: { horizontal_scroll_target: 'verticalDetector' }}
 *
 * There are three basic categories of variations for this controller.
 *
 *    1. Simple controller with one verticalDetector element
 *
 *    2. Dialog - also uses a simple controller with one verticalDetector element,
 *       but we have to detect that it is a dialog.
 *       Remember that dialog cannot have nested HorizontalScrollControllers!
 *
 *    3. Tab - since there is a common controller for all tabs in layout, each tab has its own verticalDetector.
 *       Therefore, we need to use active tab element in this controller.
 *
 * Used technologies:
 *
 * OverlayScrollbars library
 * https://kingsora.github.io/OverlayScrollbars/
 *
 * IntersectionObserver
 * https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
 */

export default class extends Controller {
  /* instance of the OverlayScrollbars */
  osInstance

  /* OverlayScrollbars scroll bar */
  osScrollBar

  /* original scroll bar styles */
  originalScrollBarStyles = {}

  /* styles for sticky scroll bar */
  fixedScrollBarStyles = {
    position: "fixed",
    bottom: 0,
    left: 0,
    width: 0,
  }

  /* Vertical container element, parent of verticalDetector */
  verticalContainer = null
  /* Vertical detector element */
  verticalDetector = null

  /* Tab element used by this controller as a target.  */
  tabElement = null
  /* Detect if this controller target is dialog */
  isDialog = false

  isTab = false

  connect() {
    super.connect()
    useZoomListener(this)
    useStableLayout(this)

    /* initialize all we need */
    this._initOS()

    /* Start with this.element, it can be overridden later */
    this.verticalContainer = this.element

    this._parentIsDialog()

    /* Recalculate horizontal scroll bounds when a nested sticky table changes width */
    this.contentWidthChangeListener = this.syncContentWidth.bind(this)
    this.element.addEventListener("table-sticky-header:content-width-change", this.contentWidthChangeListener)

    /* listen to the tab change */
    this.tabChangeListener = this._tabChanged.bind(this)
    window.addEventListener("tab:changed", this.tabChangeListener)

    /* listen to the dialog open event */
    this.dialogOpenedListener = this._dialogOpened.bind(this)
    window.addEventListener("dialog:loaded", this.dialogOpenedListener)

    this.dialogClosedListener = this._dialogClosed.bind(this)
    document.addEventListener("dialog:closed", this.dialogClosedListener)
  }

  disconnect() {
    super.disconnect()
    this.positionObserver && this.positionObserver.disconnect()
    this.osInstance.destroy()
    this.osInstance = null
    this.element.removeEventListener("table-sticky-header:content-width-change", this.contentWidthChangeListener)
    window.removeEventListener("tab:changed", this.tabChangeListener)
    window.removeEventListener("dialog:loaded", this.dialogOpenedListener)
    document.removeEventListener("dialog:closed", this.dialogClosedListener)

    /* This is how we handle the situation when the user
       clicks too quickly on secondary menu items.
       Once the controller is disconnected, we need to exit the loop
       that searches for scrollbar elements.
       Otherwise, it would continue on and eventually throw an error. */
    this._waitForScrollBarElements(true)
  }

  _parentIsDialog() {
    /* Check if the parent element of this controller is a dialog. */
    this.isDialog = this.element.parentNode.tagName.toLowerCase() === "dialog"
  }

  _dialogOpened = () => {
    /* When dialog is opened, we need to reset its position */
    const { viewport } = this.osInstance.elements()
    viewport.scrollTo({ left: 0 })
  }

  /* A wide entity dropdown inside the dialog can push the page itself sideways.
     Horizontal scrolling is this controller's concern, so the page is brought
     back here rather than from the dialog. */
  _dialogClosed = () => {
    window.scrollTo({ left: 0, behavior: "instant" })
  }

  _tabChanged = (e) => {
    this.isTab = true
    /* When tab is changed, we need to use the tab element as this controller target */
    this.tabElement = e.detail.tab

    /* reset scrollbar position between tab changes */
    const { viewport } = this.osInstance.elements()
    viewport.scrollTo({ left: 0 })

    this._waitForScrollBarElements()
  }

  _initOS() {
    /* create instance of the OverlayScrollbars */
    this.osInstance = OverlayScrollbars(
      {
        target: this.element,
      },
      {
        overflow: {
          x: "scroll",
          y: "auto",
        },
        showNativeOverlaidScrollbars: false,
        scrollbars: {
          theme: "enectiva-standard",
          visibility: "visible",
          autoHide: "never",
        },
      },
      {
        initialized: () => {
          /* we need to wait for the OverlayScrollbars to be initialized */
          this._waitForScrollBarElements()
        },
        updated: () => {
          /* we need to wait for the OverlayScrollbars to be initialized */
          this._waitForScrollBarElements()
        },
        scroll: () => {
          /* emit a custom event when the user scrolls horizontally,
             this event is used by other controllers,
             especially in TableStickyHeaderController */
          const { viewport } = this.osInstance.elements()
          this._dispatchHorizontalScroll(viewport.scrollLeft)
        },
      },
    )
  }

  _dispatchHorizontalScroll(scrollLeft) {
    const horizontalScrollEvent = new CustomEvent("horizontal-scroll", {
      detail: { scrollLeft },
    })

    this.verticalContainer.dispatchEvent(horizontalScrollEvent)
  }

  syncContentWidth() {
    if (!this.osInstance || !this.verticalDetector) return

    const { viewport } = this.osInstance.elements()
    const previousScrollLeft = viewport.scrollLeft
    const previousMaxScrollLeft = Math.max(viewport.scrollWidth - viewport.clientWidth, 0)
    const edgeTolerance = 1
    const distanceFromRightEdge = previousMaxScrollLeft - previousScrollLeft
    const wasAtRightEdge = distanceFromRightEdge <= edgeTolerance

    this._setVerticalDetectorWidth()
    this.osInstance.update(true)

    const maxScrollLeft = Math.max(viewport.scrollWidth - viewport.clientWidth, 0)
    viewport.scrollLeft = wasAtRightEdge ? maxScrollLeft : Math.min(previousScrollLeft, maxScrollLeft)
    this._dispatchHorizontalScroll(viewport.scrollLeft)
  }

  async onZoomChange() {
    await this._reinit()
  }

  async onLayoutResizeChange() {
    await this._reinit()
  }

  /* reinitialize after window resize */
  async _reinit() {
    this.positionObserver && this.positionObserver.disconnect()
    await this._observeTableStickyHeader()
    this._initOS()
  }

  _observeTableStickyHeader() {
    const timeout = 500
    return new Promise((resolve) => {
      let finished = false

      const handler = () => {
        if (finished) return
        finished = true
        resolve()
      }

      window.addEventListener("table-sticky-header:ready", handler, { once: true })

      setTimeout(() => {
        if (!finished) {
          finished = true
          window.removeEventListener("table-sticky-header:ready", handler)
          resolve()
        }
      }, timeout)
    })
  }

  /* Some elements are generated asynchronously,
     so we have to wait for them to load. */
  _waitForScrollBarElements = (killCheckExists = true) => {
    let progressTime = 0
    const timeout = 100
    const maxTimeout = 5000

    let osScrollBar
    let verticalDetector

    /* A more elegant solution would be using MutationObserver,
       but for some reason that doesn't work,
       so, I chose a different solution. */
    const checkExist = setInterval(() => {
      /* See comment in disconnect method. */
      killCheckExists && clearInterval(checkExist)

      /* this controller can have another nested HorizontalScrollControllers,
         for example inside tabs or dialogs, and they have its own
         verticalContainer and osScrollBar, so we need find direct children
         for this controller to get right corresponding elements */

      const verticalDetectorWrapper = this._findDirectChild(this.verticalContainer, "[data-overlayscrollbars-viewport]")
      if (verticalDetectorWrapper) {
        osScrollBar = this._findDirectChild(this.verticalContainer, ".os-scrollbar-horizontal")
        verticalDetector = this._findDirectChild(
          verticalDetectorWrapper,
          '[data-horizontal-scroll-target="verticalDetector"]',
        )
      }

      /* if target of this controller is tab,
         we need to find vertical detector inside this tab.
         Again, tab can have another nested HorizontalScrollControllers,
         so we need find direct children */
      if (this.tabElement) {
        verticalDetector = this._findDirectChild(this.tabElement, '[data-horizontal-scroll-target="verticalDetector"]')
      }

      /* If target of this controller is dialog,
         we cannot look for direct children,  because
         we cannot wrap dialog element with HorizontalScrollController,
         so dialog cannot have nested HorizontalScrollController
         and we can use simple querySelector */
      if (this.isDialog) {
        osScrollBar = this.verticalContainer.querySelector(".os-scrollbar-horizontal")
        verticalDetector = this.verticalContainer.querySelector('[data-horizontal-scroll-target="verticalDetector"]')
      }

      /* If we have found all elements, we can stop timeout loop */
      if (verticalDetector && this.verticalContainer && osScrollBar) {
        this.osScrollBar = osScrollBar
        this.verticalDetector = verticalDetector
        clearInterval(checkExist)
        this._constructObserver()
        this._setScrollbarWidth()
      } else {
        /* If some elements are not found, and we have exceeded maxTimeout,
           we will terminate the loop with reporting an error. */
        progressTime += timeout
        if (progressTime > maxTimeout) {
          clearInterval(checkExist)
          console.error(
            `HorizontalScrollController: Scrollbar elements not found after ${maxTimeout} milliseconds, check your layout structure.`,
          )
        }
      }
    }, timeout)
  }

  _constructObserver() {
    /* create an instance of the IntersectionObserver
       and watch changes */
    this.positionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (this._isVerticalDetector(entry) && entry.isIntersecting) {
          this._verticalDetectorEnteredViewport()
        } else if (this._isVerticalDetector(entry) && !entry.isIntersecting) {
          this._verticalDetectorLeftViewport()
        }
      })
    })
    this.positionObserver.observe(this.verticalDetector)
  }

  _isVerticalDetector(entry) {
    /* check if entry is a vertical detector */
    return entry.target.dataset.horizontalScrollTarget === "verticalDetector"
  }

  /**
   * Returns true if the global body overflow version changed since the last
   * time we checked. In that case the current IntersectionObserver event is
   * just a side-effect of changing body overflow and should be ignored.
   */
  _shouldIgnoreDueToBodyOverflow() {
    const currentVersion = document.body.dataset.bodyOverflowVersion || "0"
    if (currentVersion !== this._lastBodyOverflowVersion) {
      this._lastBodyOverflowVersion = currentVersion
      return true
    }
    return false
  }

  _verticalDetectorLeftViewport() {
    /* Ignore events caused by a body overflow change – do not touch the scrollbar. */
    if (this._shouldIgnoreDueToBodyOverflow()) return

    /* when the vertical detector exits the viewport,
       we need to fix the scroll bar to the bottom of the viewport */
    /* remove the detection class from the scroll bar
       so scrollbar isn't forced to have min-width */
    this.osScrollBar.classList.remove("osh-absolute")
    this._calculateScrollBarStyles("left")
    Object.assign(this.osScrollBar.style, this.fixedScrollBarStyles)
  }

  _verticalDetectorEnteredViewport() {
    /* Ignore events caused by a body overflow change – do not touch the scrollbar. */
    if (this._shouldIgnoreDueToBodyOverflow()) return

    /* when the vertical detector enters the viewport,
       we need to restore the original scroll bar styles */
    /* add the detection class to the scroll bar
       so scrollbar is forced to have min-width 100% */
    this.osScrollBar.classList.add("osh-absolute")
    this._calculateScrollBarStyles("entered")
    Object.assign(this.osScrollBar.style, this.originalScrollBarStyles)
  }

  _setScrollbarWidth() {
    this.osScrollBar.style.width = this.verticalContainer.offsetWidth + "px"
  }

  _calculateScrollBarStyles(enteredOrLeft = "left") {
    /* Calculate the scroll bar styles based on the vertical container */
    let { width: scrollBarWidth, left: scrollBarLeft } = this.verticalContainer.getBoundingClientRect()
    this.originalScrollBarStyles = {
      position: "absolute",
      top: "auto",
      bottom: 0,
      left: 0,
      width: scrollBarWidth + "px",
    }

    this._setVerticalDetectorWidth()

    /* Dialogs doesn't have full height of the viewport,
       so we need to calculate the top position of the scroll bar
       based on the dialog bottom position if they have fixed position */
    if (this.isDialog) {
      const dialog = this.verticalContainer.closest("dialog")

      /* Reset scrollbar width, needed on viewport resize */
      this._setScrollbarWidth()
      this.originalScrollBarStyles.width = this.verticalContainer.offsetWidth + "px"

      if (dialog?.open && enteredOrLeft === "left") {
        const dialogBottom = dialog.getBoundingClientRect().bottom
        const scrollBarHeight = this.osScrollBar.offsetHeight
        this.fixedScrollBarStyles.top = dialogBottom - scrollBarHeight + "px"
      }
    }
    this.fixedScrollBarStyles.width = scrollBarWidth + "px"
    this.fixedScrollBarStyles.left = scrollBarLeft + "px"
  }

  /* Sometimes the layout structure is too complex
     for the verticalDetector to have the necessary width.
     So we need to set the width of the verticalDetector
     according to the widest element inside the verticalContainer. */
  _setVerticalDetectorWidth() {
    const allChildren = Array.from(
      this.verticalContainer.querySelectorAll(
        '*:not([data-horizontal-scroll-target="verticalDetector"])' +
          ":not(.os-scrollbar-horizontal)" +
          ":not(.os-scrollbar-track):not(.os-scrollbar-handle)" +
          ":not([data-overlayscrollbars-viewport])" +
          ":not(.sticky-table-container)" +
          ":not(.sticky-table-header)" +
          ':not(:is([data-table-sticky-header-target="tableClone"], [data-table-sticky-header-target="tableClone"] *))' +
          ':not([data-table-sticky-header-target="firstRowDetector"])' +
          ':not([data-table-sticky-header-target="lastRowDetector"])' +
          ":not(th)",
      ),
    )

    let widestElementWidth = 0
    for (const element of allChildren) {
      const width = element.getBoundingClientRect().width
      if (width > widestElementWidth) widestElementWidth = width
    }

    if (widestElementWidth === 0) widestElementWidth = this.verticalContainer.scrollWidth

    if (!this.isTab) this.verticalDetector.style.width = widestElementWidth + "px"
  }

  /* Sometimes we need a direct descendant of an element because an element
     may contain multiple nested descendants with the same attribute. */
  _findDirectChild(element, matchingElement) {
    /* Find all direct children of the element and create array from this nodeList
       so we can use find method */
    const children = Array.from(element.children)
    /* Return the first child that matches the matchingElement selector */
    return children.find((child) => child.matches(matchingElement))
  }
}
